import {
    useEffect, useMemo, useState, type FormEvent, type MouseEvent,
} from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { activePractitionerDetails } from 'src/graphql';
import { useNavigate } from 'react-router-dom';
import MainLayout from 'src/Components/Pages/MainLayout';
import {
    Alert, PrimaryButton, SecondaryButton, Skeleton, TextInput,
} from '@luminalearning/components';
import UPDATE_API_HOOK_URL from 'src/graphql/mutations/updateApiHookUrl';
import GET_API_HOOK_URLS from 'src/graphql/queries/apiHookUrls';

type EnvironmentKey = 'live' | 'test';

type UrlValues = {
    exitRedirectUrl: string;
    completeRedirectUrl: string;
    completeNotificationHookUrl: string;
    pdfReadyNotificationHookUrl: string;
    pdfDownloadHookUrl: string;
};

type SaveStatus = {
    tone: 'Success' | 'Error';
    message: string;
    isShow: boolean;
};

const emptyUrls: UrlValues = {
    exitRedirectUrl: '',
    completeRedirectUrl: '',
    completeNotificationHookUrl: '',
    pdfReadyNotificationHookUrl: '',
    pdfDownloadHookUrl: '',
};

const DeveloperSkeleton = () => (
    <div className='pb-20'>
        <div className='mb-4'>
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={40} width={280} />
        </div>
        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={20} width={520} />
        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={20} width={380} className='mt-2' />
        <div className='mt-8 flex flex-row gap-x-4'>
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={44} width={200} />
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={44} width={200} />
        </div>
        <div className='mt-8 w-full xl:pr-4 2xl:pr-20 3xl:pr-2'>
            {[0, 1, 2, 3, 4].map((index) => (
                <div
                    key={index}
                    className='mt-6 border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'
                >
                    <div className='flex flex-col gap-y-3'>
                        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={22} width={320} />
                        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={18} width={520} />
                        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={46} width='100%' />
                        <div className='flex justify-end'>
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={40} width={120} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const Developer = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const practitionerDetails = useReactiveVar(activePractitionerDetails);
    const [activeEnvironmentTab, setActiveEnvironmentTab] = useState<EnvironmentKey>('test');
    const [liveValues, setLiveValues] = useState<UrlValues>(emptyUrls);
    const [testValues, setTestValues] = useState<UrlValues>(emptyUrls);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [statusByKey, setStatusByKey] = useState<Record<string, SaveStatus | undefined>>({});
    const [updateApiHookUrl] = useMutation(UPDATE_API_HOOK_URL);
    const [hasLoadedUrls, setHasLoadedUrls] = useState(false);

    const profileId = practitionerDetails?.id ?? null;
    const { data: apiHookUrlsData, loading: apiHookUrlsLoading } = useQuery(GET_API_HOOK_URLS, {
        variables: profileId ? { profileId } : undefined,
        skip: !profileId,
        fetchPolicy: 'network-only',
    });

    const activeValues = activeEnvironmentTab === 'live' ? liveValues : testValues;

    const fields = useMemo(() => ([
        {
            key: 'exitRedirectUrl' as keyof UrlValues,
            urlType: 'exit-redirect',
            label: intl.formatMessage({
                id: 'LP_DEVELOPER_EXIT_REDIRECT_URL',
                defaultMessage: 'Questionnaire exit redirect URL',
            }),
            description: intl.formatMessage({
                id: 'LP_DEVELOPER_EXIT_REDIRECT_URL_DESC',
                defaultMessage: 'Where users are sent if they exit the questionnaire before completion.',
            }),
            placeholder: 'https://example.com/exit',
        },
        {
            key: 'completeRedirectUrl' as keyof UrlValues,
            urlType: 'questionnaire-complete',
            label: intl.formatMessage({
                id: 'LP_DEVELOPER_COMPLETE_REDIRECT_URL',
                defaultMessage: 'Questionnaire complete redirect URL',
            }),
            description: intl.formatMessage({
                id: 'LP_DEVELOPER_COMPLETE_REDIRECT_URL_DESC',
                defaultMessage: 'Where users are sent immediately after completing the questionnaire.',
            }),
            placeholder: 'https://example.com/complete',
        },
        {
            key: 'completeNotificationHookUrl' as keyof UrlValues,
            urlType: 'questionnaire-complete-notification',
            label: intl.formatMessage({
                id: 'LP_DEVELOPER_COMPLETE_NOTIFICATION_HOOK_URL',
                defaultMessage: 'Questionnaire complete notification hook URL',
            }),
            description: intl.formatMessage({
                id: 'LP_DEVELOPER_COMPLETE_NOTIFICATION_HOOK_URL_DESC',
                defaultMessage: 'Webhook triggered when a questionnaire is completed. Sent via POST request.',
            }),
            placeholder: 'https://example.com/hooks/complete',
        },
        {
            key: 'pdfReadyNotificationHookUrl' as keyof UrlValues,
            urlType: 'portrait-notification',
            label: intl.formatMessage({
                id: 'LP_DEVELOPER_PDF_READY_NOTIFICATION_HOOK_URL',
                defaultMessage: 'PDF ready notification hook URL',
            }),
            description: intl.formatMessage({
                id: 'LP_DEVELOPER_PDF_READY_NOTIFICATION_HOOK_URL_DESC',
                defaultMessage: 'Webhook triggered when the PDF portrait is ready. Sent via POST request.',
            }),
            placeholder: 'https://example.com/hooks/pdf-ready',
        },
        {
            key: 'pdfDownloadHookUrl' as keyof UrlValues,
            urlType: 'pdf-hook',
            label: intl.formatMessage({
                id: 'LP_DEVELOPER_PDF_DOWNLOAD_HOOK_URL',
                defaultMessage: 'PDF portrait hook URL',
            }),
            description: intl.formatMessage({
                id: 'LP_DEVELOPER_PDF_DOWNLOAD_HOOK_URL_DESC',
                defaultMessage: 'Webhook triggered when the PDF portrait is ready, it will sent the PDF portrait. Sent via POST request.',
            }),
            placeholder: 'https://example.com/hooks/pdf-download',
        },
    ]), [intl]);

    const updateValue = (environment: EnvironmentKey, key: keyof UrlValues, value: string) => {
        const setValues = environment === 'live' ? setLiveValues : setTestValues;
        setValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSubmit = async (
        event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
        environment: EnvironmentKey,
        key: keyof UrlValues
    ) => {
        event.preventDefault();
        event.stopPropagation();
        const values = environment === 'live' ? liveValues : testValues;
        const submittedValue = values[key];
        const statusKey = `${environment}-${String(key)}`;
        const environmentValue = environment === 'live' ? 'production' : 'test';

        if (submittedValue.trim().length === 0) {
            return;
        }

        if (!practitionerDetails?.id) {
            setStatusByKey((prev) => ({
                ...prev,
                [statusKey]: {
                    tone: 'Error',
                    message: intl.formatMessage({
                        id: 'LP_DEVELOPER_MISSING_PROFILE',
                        defaultMessage: 'Unable to save: missing practitioner profile.',
                    }),
                    isShow: true,
                },
            }));

            setTimeout(() => {
                setStatusByKey((prev) => ({
                    ...prev,
                    [statusKey]: undefined,
                }));
            }, 1500);
            return;
        }

        const field = fields.find((item) => item.key === key);
        if (!field) {
            return;
        }

        setSavingKey(statusKey);
        setStatusByKey((prev) => ({
            ...prev,
            [statusKey]: undefined,
        }));

        try {
            const { data } = await updateApiHookUrl({
                variables: {
                    profileId: practitionerDetails.id,
                    urlType: field.urlType,
                    url: submittedValue,
                    environment: environmentValue,
                },
            });

            const response = data?.updateApiHookUrl;
            if (response === 'SUCCESS') {
                setStatusByKey((prev) => ({
                    ...prev,
                    [statusKey]: {
                        tone: 'Success',
                        message: intl.formatMessage({
                            id: 'LP_DEVELOPER_SAVE_SUCCESS',
                            defaultMessage: 'Saved successfully.',
                        }),
                        isShow: true,
                    },
                }));
                return;
            }

            const errorMessage = response === 'UNAUTHORIZED'
                ? intl.formatMessage({
                    id: 'LP_DEVELOPER_SAVE_UNAUTHORIZED',
                    defaultMessage: 'You are not authorized to update this URL.',
                })
                : intl.formatMessage({
                    id: 'LP_DEVELOPER_SAVE_FAILED',
                    defaultMessage: 'Failed to save. Please try again.',
                });

            setStatusByKey((prev) => ({
                ...prev,
                [statusKey]: {
                    tone: 'Error',
                    message: errorMessage,
                    isShow: true,
                },
            }));
        } catch (error) {
            setStatusByKey((prev) => ({
                ...prev,
                [statusKey]: {
                    tone: 'Error',
                    message: intl.formatMessage({
                        id: 'LP_DEVELOPER_SAVE_FAILED',
                        defaultMessage: 'Failed to save. Please try again.',
                    }),
                    isShow: true,
                },
            }));
        } finally {
            setTimeout(() => {
                setStatusByKey((prev) => ({
                    ...prev,
                    [statusKey]: undefined,
                }));
            }, 1500);
            setSavingKey((current) => (current === statusKey ? null : current));
        }
    };

    useEffect(() => {
        document.title = intl.formatMessage({ id: 'LP_DEVELOPER_DASHBOARD_TITLE', defaultMessage: 'Developer Dashboard' });
    }, [intl]);

    useEffect(() => {
        if (practitionerDetails !== null && practitionerDetails.isApiEnabled !== true) {
            navigate('/');
        }
    }, [practitionerDetails, navigate]);

    useEffect(() => {
        setHasLoadedUrls(false);
        setLiveValues(emptyUrls);
        setTestValues(emptyUrls);
    }, [profileId]);

    useEffect(() => {
        if (!profileId || apiHookUrlsLoading || hasLoadedUrls) {
            return;
        }

        const response = apiHookUrlsData?.getApiHookUrls;

        const mapResponseToValues = (environment?: {
            pdfDownloadUrl?: string | null;
            pdfReadyNotificationUrl?: string | null;
            questionnaireCompleteNotificationUrl?: string | null;
            questionnaireCompleteUrl?: string | null;
            questionnaireExitUrl?: string | null;
        } | null) => ({
            exitRedirectUrl: environment?.questionnaireExitUrl ?? '',
            completeRedirectUrl: environment?.questionnaireCompleteUrl ?? '',
            completeNotificationHookUrl: environment?.questionnaireCompleteNotificationUrl ?? '',
            pdfReadyNotificationHookUrl: environment?.pdfReadyNotificationUrl ?? '',
            pdfDownloadHookUrl: environment?.pdfDownloadUrl ?? '',
        });

        setLiveValues(mapResponseToValues(response?.production));
        setTestValues(mapResponseToValues(response?.test));
        setHasLoadedUrls(true);
    }, [apiHookUrlsData, apiHookUrlsLoading, hasLoadedUrls, profileId]);

    return (
        <MainLayout>
            {(!profileId || apiHookUrlsLoading || !hasLoadedUrls) ? <DeveloperSkeleton /> : (
                <div className='pb-20'>
                    <h1 className='text-headline-lg font-bold' style={{ lineHeight: '45px' }}>
                        {
                            intl.formatMessage({
                                id: 'LP_DEVELOPER_DASHBOARD_TITLE',
                                defaultMessage: 'Developer Dashboard',
                            })
                        }
                    </h1>
                    <div className='mt-4 text-paragraph-reg max-w-3xl'>
                        {
                            intl.formatMessage({
                                id: 'LP_DEVELOPER_ENVIRONMENT_DESCRIPTION',
                                defaultMessage: 'Configure separate webhook and redirect URLs for Live and Test environments.',
                            })
                        }
                    </div>
                    <div className='mt-8 flex flex-row gap-x-4' role='tablist' aria-label={
                        intl.formatMessage({
                            id: 'LP_DEVELOPER_ENVIRONMENT_TABS',
                            defaultMessage: 'Environment tabs',
                        })
                    }>
                        {([
                            { key: 'test', label: intl.formatMessage({ id: 'LP_TEST_ENVIRONMENT', defaultMessage: 'Test environment' }) },
                            { key: 'live', label: intl.formatMessage({ id: 'LP_LIVE_ENVIRONMENT', defaultMessage: 'Live environment' }) },
                        ] as const).map((tab) => (
                            activeEnvironmentTab === tab.key ? (
                                <PrimaryButton
                                    key={tab.key}
                                    type='button'
                                    text={tab.label}
                                    onClick={() => setActiveEnvironmentTab(tab.key)}
                                    className='px-6 py-3 rounded-full text-label-reg font-bold'
                                />
                            ) : (
                                <SecondaryButton
                                    key={tab.key}
                                    type='button'
                                    className='px-6 py-3 rounded-full text-label-reg font-bold'
                                    onClick={() => setActiveEnvironmentTab(tab.key)}
                                    text={tab.label}
                                />
                            )
                        ))}
                    </div>
                    <div className='mt-8 w-full xl:pr-4 2xl:pr-20 3xl:pr-2'>
                        {fields.map((field) => {
                            const statusKey = `${activeEnvironmentTab}-${field.key}`;
                            const status = statusByKey[statusKey];
                            const isSaving = savingKey === statusKey;

                            return (
                                <form
                                    key={statusKey}
                                    className='mt-6 border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'
                                    onSubmit={(event) => handleSubmit(event, activeEnvironmentTab, field.key)}
                                >
                                    <div className='flex flex-col gap-y-3'>
                                        <span className='text-headline-reg font-bold'>
                                            {field.label}
                                        </span>
                                        <span className='text-paragraph-reg'>
                                            {field.description}
                                        </span>
                                        {status && status.isShow && (
                                            <Alert type={status.tone} message={status.message} />
                                        )}
                                        <TextInput
                                            value={activeValues[field.key]}
                                            placeholder={field.placeholder}
                                            setValue={(value) => updateValue(activeEnvironmentTab, field.key, value)}
                                            className='w-full'
                                            isDisabled={isSaving}
                                        />
                                        <div className='flex justify-end'>
                                            <PrimaryButton
                                                type='submit'
                                                isDisabled={isSaving}
                                                isLoading={isSaving}
                                                text={intl.formatMessage({
                                                    id: 'LP_SAVE',
                                                    defaultMessage: 'Save',
                                                })}
                                                onClick={(event) => handleSubmit(
                                                    event, activeEnvironmentTab, field.key
                                                )}
                                            />
                                        </div>
                                    </div>
                                </form>
                            );
                        })}
                    </div>
                </div>
            )}
        </MainLayout>
    );
};

export default Developer;
